import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useEffect, useState, type RefObject } from "react";

/** A dialog on the stack, and the dialog it is rendered inside, if any. */
export interface StackedDialog {
  readonly parent: StackedDialog | null;
}

/** Hands each dialog to the dialogs rendered inside it; context crosses the portal. */
export const DialogStackContext = createContext<StackedDialog | null>(null);
const MODAL_DIALOG = '[role="dialog"][aria-modal="true"]';

// UI that sits above every dialog: the chat widget (ChatWidget) and the login dialog (LoginModal).
const ABOVE_DIALOGS = "[data-above-modal]";

// The open dialogs and their elements in the order they opened, the top one last.
const openDialogs: Array<{
  dialog: StackedDialog;
  node: HTMLElement;
}> = [];
function isNestedIn(dialog: StackedDialog, ancestor: StackedDialog): boolean {
  for (let parent = dialog.parent; parent; parent = parent.parent) {
    if (parent === ancestor) return true;
  }
  return false;
}

/**
 * Keeps the calling dialog on the stack of open dialogs while `open`, with
 * `nodeRef` as its element, and returns its place, which it provides through
 * `DialogStackContext` to the dialogs rendered inside it.
 *
 * A dialog that opens goes on top, but always below the dialogs rendered
 * inside it: when a dialog and one nested in it open in the same render, React
 * runs the nested one's effects first, so that one is on the stack already.
 * Document order cannot decide either, because React inserts the nested
 * dialog's portal before its parent's. Pinned by Modal.test.tsx.
 */
export function useDialogStack(open, nodeRef) {
  const $ = _c(7);
  const parent = useContext(DialogStackContext);
  let t0;
  if ($[0] !== parent) {
    t0 = () => ({
      parent
    });
    $[0] = parent;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [dialog] = useState(t0);
  let t1;
  let t2;
  if ($[2] !== dialog || $[3] !== nodeRef || $[4] !== open) {
    t1 = () => {
      const node = nodeRef.current;
      if (!open || !node) {
        return;
      }
      const entry = {
        dialog,
        node
      };
      const firstNested = openDialogs.findIndex(other => isNestedIn(other.dialog, dialog));
      openDialogs.splice(firstNested === -1 ? openDialogs.length : firstNested, 0, entry);
      return () => {
        const index = openDialogs.indexOf(entry);
        if (index !== -1) {
          openDialogs.splice(index, 1);
        }
      };
    };
    t2 = [open, dialog, nodeRef];
    $[2] = dialog;
    $[3] = nodeRef;
    $[4] = open;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  return dialog;
}

/** The element of the dialog on top, if any dialog is open. */
export function topDialogElement(): HTMLElement | undefined {
  return openDialogs[openDialogs.length - 1]?.node;
}

/**
 * Whether `dialog` is on top. A modal dialog that is not on the stack and comes
 * after it in the document is above it: Turnstile's challenge overlay, or an
 * inline dialog rendered inside it.
 */
export function isTopDialog(dialog: StackedDialog): boolean {
  const top = openDialogs[openDialogs.length - 1];
  if (top?.dialog !== dialog) return false;
  const dialogs = Array.from(document.querySelectorAll(MODAL_DIALOG));
  return dialogs.slice(dialogs.indexOf(top.node) + 1).every(other => openDialogs.some(open => open.node === other));
}

/**
 * Whether `target` is in UI above every dialog on the stack, whose focus and Escape the dialog
 * on top leaves alone: an element marked `data-above-modal`, or a modal dialog outside every
 * dialog on the stack. The login dialog renders inline, before the dialogs' portals, so document
 * order cannot say it is above; that the user moved into it does. Pinned by Modal.test.tsx,
 * ChatWidget.modal-focus.test.tsx and LoginModal.test.tsx.
 */
export function isAboveDialogs(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest(ABOVE_DIALOGS)) return true;
  const dialog = target.closest(MODAL_DIALOG);
  return dialog !== null && !openDialogs.some(open => open.node.contains(dialog));
}